/**
 * This module provides the form handler for the Emergency-Power-Settings of a Storage device.
 *
 * It uses a work in progress implementation of the Formik form handler.
 *
 */
import React from 'react'
import { Typography } from '@mui/material'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import SettingsBox from 'src/Elements/Data/SettingsBox'
import { EmergencyPowerSettingsModel } from 'src/Libs/emergency-power'
import SingleToggleSettingBox from 'src/Elements/SystemSettings/forms/setting-boxes/SingleToggleSettingBox'
import SettingsFormContainer from 'src/Elements/SystemSettings/forms/SettingsFormContainer'
import { NumberField } from 'src/Libs/inputs/formik'
import { UnitTypes } from 'src/Libs/types'
import { Alert } from '@e3dc-react/shell/Elements/Alert'

const MINIMUM_EMERGENCY_POWER_DELAY_IN_S = 5
const MAXIMUM_EMERGENCY_POWER_DELAY_IN_S = 60

interface Props {
  model: EmergencyPowerSettingsModel
  disabled?: boolean
  showWallboxSettings: boolean
}

/**
 * TODO: Consider to pass the following props separately from a higher component (e.g. page) to better fit to the atom-design-principle:
 * (1) disabled (2) displayTooltip (3) titleKey
 * note: currently (1) and (2) both depend on disabled, which should be separated
 */
const EmergencyPowerSettingsForm: React.VFC<Props> = ({ model, showWallboxSettings, disabled = false }) => {
  const { t } = useCustomTranslation()

  const isUsageDisabled = !model.emsIsPossible || disabled

  return (
    <SettingsFormContainer model={model}>
      {isUsageDisabled && <Alert sx={{ mb: 5 }} severity="warning" text={t('systemSettings.emergencyPower.settings.alerts.updateIsDisabled')} />}
      <Typography sx={{ mb: 4 }} variant="body1">
        {t('systemSettings.emergencyPower.settings.delaySection.description')}
      </Typography>
      <SingleToggleSettingBox
        data-qa="settings-box-partial-grid"
        name="partialGrid"
        title={t('systemSettings.emergencyPower.settings.partialGrid')}
        disabled={isUsageDisabled}
      />
      <SettingsBox
        data-qa="settings-box-delay"
        title={t('systemSettings.emergencyPower.settings.delay')}
        disabled={isUsageDisabled}
        suffix={
          <NumberField
            name="delay"
            min={MINIMUM_EMERGENCY_POWER_DELAY_IN_S}
            max={MAXIMUM_EMERGENCY_POWER_DELAY_IN_S}
            label={t('systemSettings.emergencyPower.settings.delay')}
            suffix="s"
            disabled={isUsageDisabled}
          />
        }
      />
      <SettingsBox
        data-qa="settings-box-reserve"
        title={t('systemSettings.emergencyPower.settings.reserve')}
        disabled={isUsageDisabled}
        suffix={
          <NumberField
            name="reserve"
            min={0}
            label={t('systemSettings.emergencyPower.settings.reserve')}
            suffix={UnitTypes.wattHour}
            disabled={isUsageDisabled}
          />
        }
      />
      <SingleToggleSettingBox
        data-qa="settings-box-wallbox-allow"
        name="wallboxAllow"
        title={t('systemSettings.emergencyPower.settings.wallbox.allow')}
        disabled={isUsageDisabled}
      />
      {showWallboxSettings && (
        <SettingsBox
          data-qa="settings-box-wallbox-min-ep-power"
          title={t('systemSettings.emergencyPower.settings.wallbox.minEpPower')}
          disabled={isUsageDisabled}
          suffix={
            <NumberField
              name="wallboxMinEpPower"
              min={0}
              label={t('systemSettings.emergencyPower.settings.wallbox.minEpPower')}
              suffix={UnitTypes.watt}
              disabled={isUsageDisabled}
            />
          }
        />
      )}
      {showWallboxSettings && (
        <SettingsBox
          data-qa="settings-box-wallbox-max-ep-power"
          title={t('systemSettings.emergencyPower.settings.wallbox.maxEpPower')}
          disabled={isUsageDisabled}
          suffix={
            <NumberField
              name="wallboxMaxEpPower"
              min={0}
              label={t('systemSettings.emergencyPower.settings.wallbox.maxEpPower')}
              suffix={UnitTypes.watt}
              disabled={isUsageDisabled}
            />
          }
        />
      )}
      <SettingsBox
        data-qa="settings-box-wallbox-energy"
        title={t('systemSettings.emergencyPower.settings.wallbox.energy')}
        disabled={isUsageDisabled}
        suffix={
          <NumberField
            name="wallboxEnergy"
            min={0}
            label={t('systemSettings.emergencyPower.settings.wallbox.energy')}
            suffix={UnitTypes.wattHour}
            disabled={isUsageDisabled}
          />
        }
      />
    </SettingsFormContainer>
  )
}

export default EmergencyPowerSettingsForm
